/**
 * When this build serves only one product (REACT_APP_PRODUCTS set, mirroring
 * the backend's PRODUCTS env var), the app must never render the other
 * product's shell — its backend has no routes for it, so any dead-end
 * (sidebar click, typed URL, bookmark, OAuth redirect) would otherwise render
 * a page that can only fail to fetch its data.
 *
 * Unset (both products, today's default) returns null: mode stays whatever
 * the URL/localStorage would otherwise resolve to, unrestricted.
 *
 * @example
 * getLockedAppMode("mantis");
 * // "business"
 *
 * @example
 * getLockedAppMode("dataeng");
 * // "developer"
 *
 * @example
 * getLockedAppMode("");
 * // null
 */
export const getLockedAppMode = (
  productsEnv = process.env.REACT_APP_PRODUCTS
) => {
  const products = String(productsEnv || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (products.length === 0 || products.length > 1) {
    return null;
  }

  if (products[0] === "mantis") {
    return "business";
  }
  if (products[0] === "dataeng") {
    return "developer";
  }
  return null;
};

/**
 * public/index.html hardcodes Task Mantis favicon/title/description as static
 * assets (CRA copies public/ as-is; there's no per-build templating for it).
 * On a Starmesh-locked build, swap them at runtime instead so the browser
 * tab/bookmark matches the in-app branding already handled by getLockedAppMode().
 */
export const applyLockedBrandToDocument = (
  productsEnv = process.env.REACT_APP_PRODUCTS
) => {
  if (typeof document === "undefined") {
    return;
  }
  if (getLockedAppMode(productsEnv) !== "developer") {
    return;
  }

  // Shared core components whose popovers portal to document.body (see
  // LookbackPeriodControl) cannot be themed from inside the product tree, so
  // they read their palette from custom properties keyed off this attribute.
  document.documentElement.setAttribute("data-product", "dataeng");

  document.title = "Starmesh";

  const descriptionMeta = document.querySelector('meta[name="description"]');
  if (descriptionMeta) {
    descriptionMeta.setAttribute(
      "content",
      "Starmesh — connect your data sources and destinations with AI-built transforms."
    );
  }

  const iconSwaps = [
    ['link[rel="shortcut icon"]', "/starmesh-favicon.ico"],
    ['link[rel="icon"][sizes="32x32"]', "/starmesh-icon32.png"],
    ['link[rel="icon"][sizes="16x16"]', "/starmesh-icon16.png"],
    ['link[rel="apple-touch-icon"]', "/starmesh-icon128.png"],
  ];
  for (const [selector, href] of iconSwaps) {
    const link = document.querySelector(selector);
    if (link) {
      link.setAttribute("href", href);
    }
  }
};
